/**
 * S012 chat-list domain model.
 *
 * Figma authority: R4VYIdXEv7HYIXtpDMEvHY, node 38:2717.
 * The model is presentation-neutral: it contains no React, browser, or
 * navigation values. Runtime constructors validate and deep-freeze records so
 * fixture and future API adapters cannot leak mutable snapshots to callers.
 */

export const MIN_PARTICIPANT_AGE = 18;
export const MAX_PARTICIPANT_AGE = 120;

export interface ConversationAvatar {
  readonly initials: string;
  readonly backgroundColor: string;
  readonly sourceNodeId: string;
}

export interface ConversationParticipant {
  readonly id: string;
  readonly displayName: string;
  readonly age: number;
  readonly city: string;
  readonly avatar: ConversationAvatar;
  readonly isOnline: boolean;
}

export interface ConversationSummary {
  readonly channelId: string;
  readonly participant: ConversationParticipant;
  readonly lastMessage: string;
  readonly lastActivityAt: string;
  readonly unreadCount: number;
}

export type NonEmptyConversations = readonly [
  ConversationSummary,
  ...ConversationSummary[],
];

export type ChatListModel =
  | {
      readonly status: 'loading';
      readonly activeLoadId: number;
    }
  | {
      readonly status: 'ready';
      readonly activeLoadId: number;
      readonly conversations: NonEmptyConversations;
    }
  | {
      readonly status: 'empty';
      readonly activeLoadId: number;
    }
  | {
      readonly status: 'inactive_sponsor';
      readonly activeLoadId: number;
    }
  | {
      readonly status: 'error';
      readonly activeLoadId: number;
    };

function assertLoadId(loadId: number): void {
  if (!Number.isSafeInteger(loadId) || loadId < 0) {
    throw new RangeError('chat-list loadId must be a non-negative safe integer');
  }
}

function copyRequiredString(
  value: string,
  field: string,
  maximumLength: number,
): string {
  if (typeof value !== 'string') {
    throw new TypeError(`${field} must be a string`);
  }

  const copy = `${value}`;
  if (copy.trim().length === 0 || copy.length > maximumLength) {
    throw new RangeError(`${field} must be non-empty and at most ${maximumLength} characters`);
  }
  return copy;
}

function copyAvatar(avatar: ConversationAvatar): ConversationAvatar {
  const initials = copyRequiredString(avatar.initials, 'avatar.initials', 2);
  if (Array.from(initials).length > 2) {
    throw new RangeError('avatar.initials must contain at most two Unicode characters');
  }

  const backgroundColor = copyRequiredString(
    avatar.backgroundColor,
    'avatar.backgroundColor',
    7,
  );
  if (!/^#[0-9a-fA-F]{6}$/.test(backgroundColor)) {
    throw new RangeError('avatar.backgroundColor must be a six-digit hex color');
  }

  const sourceNodeId = copyRequiredString(
    avatar.sourceNodeId,
    'avatar.sourceNodeId',
    64,
  );
  if (!/^\d+:\d+$/.test(sourceNodeId)) {
    throw new RangeError('avatar.sourceNodeId must be a Figma node identity');
  }

  return Object.freeze({ initials, backgroundColor, sourceNodeId });
}

function copyParticipant(
  participant: ConversationParticipant,
): ConversationParticipant {
  const age = participant.age;
  if (
    !Number.isFinite(age)
    || !Number.isInteger(age)
    || age < MIN_PARTICIPANT_AGE
    || age > MAX_PARTICIPANT_AGE
  ) {
    throw new RangeError(
      `participant.age must be an integer from ${MIN_PARTICIPANT_AGE} to ${MAX_PARTICIPANT_AGE}`,
    );
  }

  if (typeof participant.isOnline !== 'boolean') {
    throw new TypeError('participant.isOnline must be boolean');
  }

  return Object.freeze({
    id: copyRequiredString(participant.id, 'participant.id', 128),
    displayName: copyRequiredString(
      participant.displayName,
      'participant.displayName',
      100,
    ),
    age,
    city: copyRequiredString(participant.city, 'participant.city', 160),
    avatar: copyAvatar(participant.avatar),
    isOnline: participant.isOnline,
  });
}

/** Validate, clone, and deeply freeze one conversation record. */
export function createConversationSummary(
  conversation: ConversationSummary,
): ConversationSummary {
  const unreadCount = conversation.unreadCount;
  if (
    !Number.isFinite(unreadCount)
    || !Number.isInteger(unreadCount)
    || unreadCount < 0
  ) {
    throw new RangeError('conversation.unreadCount must be an integer greater than or equal to zero');
  }

  const lastActivityAt = copyRequiredString(
    conversation.lastActivityAt,
    'conversation.lastActivityAt',
    64,
  );
  if (!Number.isFinite(Date.parse(lastActivityAt))) {
    throw new RangeError('conversation.lastActivityAt must be a valid timestamp');
  }

  return Object.freeze({
    channelId: copyRequiredString(conversation.channelId, 'conversation.channelId', 128),
    participant: copyParticipant(conversation.participant),
    lastMessage: copyRequiredString(conversation.lastMessage, 'conversation.lastMessage', 1_000),
    lastActivityAt,
    unreadCount,
  });
}

/** Return a fresh, deeply frozen conversation snapshot. */
export function copyConversationSummaries(
  conversations: readonly ConversationSummary[],
): readonly ConversationSummary[] {
  if (!Array.isArray(conversations)) {
    throw new TypeError('conversations must be an array');
  }
  return Object.freeze(conversations.map(createConversationSummary));
}

export function createLoadingModel(activeLoadId: number): ChatListModel {
  assertLoadId(activeLoadId);
  return Object.freeze({ status: 'loading', activeLoadId });
}

/** Normalize an empty success to `empty`; `ready` is always non-empty. */
export function createLoadedModel(
  activeLoadId: number,
  conversations: readonly ConversationSummary[],
): ChatListModel {
  assertLoadId(activeLoadId);
  const copied = copyConversationSummaries(conversations);
  if (copied.length === 0) {
    return Object.freeze({ status: 'empty', activeLoadId });
  }
  return Object.freeze({
    status: 'ready',
    activeLoadId,
    conversations: copied as NonEmptyConversations,
  });
}

export const initialChatListModel: ChatListModel = createLoadingModel(0);
